import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Input, Button, Tooltip } from 'antd';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ReadOnlyGuard } from '@spark';
import { colors } from '@/shared/tokens/colors';
import { sanitizeProjectName } from './projectNameUtils';
import { deploy_in_progress, undeploy_in_progress } from '@/shared/constants/taskStatuses';

const { TextArea } = Input;

export const EditProjectModal = ({
    isOpen,
    onClose,
    onSave,
    projectData,
    allUsecases = [],
    loading = false
}) => {
    const [projectName, setProjectName] = useState("");
    const [projectGoal, setProjectGoal] = useState("");
    const [projectNameError, setProjectNameError] = useState(false);

    // Initialize form with project data when modal opens
    useEffect(() => {
        if (isOpen && projectData) {
            setProjectName(sanitizeProjectName(projectData.group_name || ""));
            setProjectGoal(projectData.project_goal || "");
            setProjectNameError(false);
        }
    }, [isOpen, projectData]);

    const resetForm = () => {
        setProjectName("");
        setProjectGoal("");
        setProjectNameError(false);
    };

    // Extract all usecases from the project's sub_groups, or filter from allUsecases by group_name
    const allProjectTasks = useMemo(() => {
        // First, try to get tasks from projectData.sub_groups (when called from ProjectGroup)
        if (projectData?.sub_groups && projectData.sub_groups.length > 0) {
            return (projectData.sub_groups || []).flatMap(subGroup => subGroup.usecases || []);
        }
        // Otherwise, filter allUsecases by group_name (when called from GroupedFlatTableView)
        if (projectData?.group_name && allUsecases.length > 0) {
            return allUsecases.filter(usecase => usecase.group_name === projectData.group_name);
        }
        return [];
    }, [projectData, allUsecases]);

    // Check if any task in the project is deployed or has deployment in progress
    const isDeploymentBlocked = useMemo(() => {
        return allProjectTasks.some(task =>
            task.is_deployed ||
            task.status === deploy_in_progress ||
            task.status === undeploy_in_progress
        );
    }, [allProjectTasks]);

    const handleSave = () => {
        if (!projectName.trim()) {
            setProjectNameError(true);
            return;
        }
        setProjectNameError(false);

        // If deployment is blocked, keep the original project name
        const finalProjectName = isDeploymentBlocked
            ? (projectData?.group_name || projectName)
            : sanitizeProjectName(projectName, { trimEnd: true });

        onSave({
            group_name: finalProjectName,
            project_goal: projectGoal
        });
    };

    const handleCancel = () => {
        onClose();
        resetForm();
    };

    return (
        <Modal
            open={isOpen}
            onCancel={handleCancel}
            footer={null}
            width={600}
            centered
            destroyOnClose
            closable={false}
            styles={{
                content: {
                    width: '600px',
                    maxWidth: '600px',
                    margin: '0 auto',
                    borderRadius: 12,
                    padding: 0
                }
            }}
        >
            <div style={{ padding: '24px 24px 20px 24px' }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 8
                }}>
                    <div>
                        <div style={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: '#1F2937',
                            marginBottom: 4
                        }}>
                            Edit Project Details
                        </div>
                        <div style={{
                            color: '#6B7280',
                            fontSize: 14,
                            marginBottom: 32
                        }}>
                            Edit the details of your project
                        </div>
                    </div>
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={handleCancel}
                        style={{
                            border: 'none',
                            boxShadow: 'none',
                            color: '#9CA3AF',
                            fontSize: 14,
                            padding: 4,
                            minWidth: 'auto'
                        }}
                    />
                </div>

                {/* Form */}
                <div>
                    <div style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        fontSize: 14,
                        color: '#374151',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                    }}>
                        Project Name <span style={{ color: '#3B82F6' }}>*</span>
                        {isDeploymentBlocked && (
                            <Tooltip title="Cannot edit project name when one or more tasks are deployed">
                                <InfoCircleOutlined style={{ color: '#A0AEC0', fontSize: 16 }} />
                            </Tooltip>
                        )}
                    </div>
                    <Input
                        placeholder="Enter project name"
                        value={projectName}
                        onChange={e => {
                            if (isDeploymentBlocked) {
                                return; // Prevent changes if deployment is blocked
                            }
                            const value = sanitizeProjectName(e.target.value);
                            setProjectName(value);
                            setProjectNameError(false);
                        }}
                        disabled={isDeploymentBlocked}
                        style={{
                            marginBottom: projectNameError ? 4 : 24,
                            fontSize: 16,
                            borderRadius: 6,
                            border: projectNameError ? '1px solid #EF4444' : `1px solid ${colors.light_blue}`
                        }}
                        status={projectNameError ? 'error' : ''}
                    />
                    {projectNameError && (
                        <div style={{
                            color: '#EF4444',
                            marginBottom: 20,
                            fontSize: 13
                        }}>
                            Project Name is required
                        </div>
                    )}

                    <div style={{
                        fontWeight: 500,
                        marginBottom: 8,
                        fontSize: 14,
                        color: '#374151'
                    }}>
                        Project Goal
                    </div>
                    <TextArea
                        placeholder="Enter project goal"
                        value={projectGoal}
                        onChange={e => setProjectGoal(e.target.value)}
                        style={{
                            marginBottom: 32,
                            minHeight: 120,
                            fontSize: 16,
                            borderRadius: 6,
                            border: `1px solid ${colors.light_blue}`,
                            resize: 'none'
                        }}
                    />

                    {/* Footer Buttons */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 12,
                        paddingTop: 20,
                        borderTop: `1px solid #E5E7EB`,
                        marginLeft: -24,
                        marginRight: -24,
                        paddingLeft: 24,
                        paddingRight: 24
                    }}>
                        <Button
                            onClick={handleCancel}
                            disabled={loading}
                            style={{
                                height: 40,
                                padding: '0 24px',
                                border: `1px solid ${colors.buttonsBlue}`,
                                borderRadius: 6,
                                fontSize: 16,
                                color: colors.buttonsBlue,
                                backgroundColor: '#FFFFFF'
                            }}
                        >
                            Cancel
                        </Button>
                        <ReadOnlyGuard>
                            <Button
                                onClick={handleSave}
                                loading={loading}
                                style={{
                                    height: 40,
                                    padding: '0 24px',
                                    border: 'none',
                                    borderRadius: 6,
                                    fontSize: 16,
                                    color: '#FFFFFF',
                                    backgroundColor: '#2563EB'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.target.style.backgroundColor = '#1D4ED8';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!loading) {
                                        e.target.style.backgroundColor = '#2563EB';
                                    }
                                }}
                            >
                                Save
                            </Button>
                        </ReadOnlyGuard>
                    </div>
                </div>
            </div>
        </Modal>
    );
}; 